import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quotesService } from '../../services/quotesService';
import { workflowStagesService } from '../../services';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';
import QuoteMessagesModal from './QuoteMessagesModal';
import { formatDateRange } from '../../utils/dateUtils';
import { useResponsive } from '../../hooks/useResponsive';
import travelieTheme from '../../styles/travelieTheme';

// Week 1: QuotesList TravelieTheme Migration - June 22, 2026

const QuotesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isDesktopOrWide } = useResponsive();
  
  // Helper function for button styling
  const getButtonStyle = (type, size = 'medium', disabled = false) => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: '#EF4444', color: '#FFFFFF' },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' },
      info: { ...baseStyle, background: '#3B9EFF', color: '#FFFFFF' },
      purple: { ...baseStyle, background: '#8B5CF6', color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };

  // Helper styles for inputs
  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [duplicatingQuoteId, setDuplicatingQuoteId] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedQuoteForConversion, setSelectedQuoteForConversion] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedQuoteForPaymentDetails, setSelectedQuoteForPaymentDetails] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedQuoteForStage, setSelectedQuoteForStage] = useState(null);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedQuoteForMessages, setSelectedQuoteForMessages] = useState(null);

  useEffect(() => {
    fetchQuotes();
    fetchWorkflowStages();
  }, []);

  // Refresh workflow stages when page gains focus (user returns from another page)
  useEffect(() => {
    const handleFocus = () => {
      fetchWorkflowStages();
    };

    window.addEventListener('focus', handleFocus);
    
    // Also listen for visibility change (when tab becomes active)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchWorkflowStages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await quotesService.getAll();
      setQuotes(data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (quoteId) => {
    if (duplicatingQuoteId) return;
    setDuplicatingQuoteId(quoteId);
    try {
      await quotesService.duplicate(quoteId);
      setSearchTerm('');
      setStageFilter('all');
      setSourceFilter('all');
      await fetchQuotes();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error duplicating quote:', error);
      alert('Failed to duplicate quote');
    } finally {
      setDuplicatingQuoteId(null);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) {
      return;
    }

    try {
      await quotesService.delete(quoteId);
      setQuotes(quotes.filter(q => q.id !== quoteId));
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await quotesService.send(quoteId, {});
      // Update the quote status locally
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'sent' } : q
      ));
      alert('Quote sent successfully!');
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote');
    }
  };

  const handleMarkAsSent = async (quoteId) => {
    try {
      await quotesService.markAsSent(quoteId);
      fetchQuotes(); // Refresh the list
    } catch (error) {
      console.error('Error marking quote as sent:', error);
      setError('Failed to mark quote as sent');
    }
  };

  const fetchWorkflowStages = async () => {
    try {
      const stages = await workflowStagesService.getStages();
      setWorkflowStages(stages);
    } catch (error) {
      console.error('Failed to fetch workflow stages:', error);
    }
  };

  const handleMoveStage = (quote) => {
    setSelectedQuoteForStage(quote);
    setShowStageModal(true);
  };

  const handleViewMessages = (quote) => {
    setSelectedQuoteForMessages(quote);
    setShowMessagesModal(true);
  };

  const closeMessagesModal = () => {
    setShowMessagesModal(false);
    setSelectedQuoteForMessages(null);
  };

  const handleMoveQuoteStage = async (stageId, reason = null) => {
    if (!selectedQuoteForStage) return;

    try {
      await quotesService.moveStage(selectedQuoteForStage.id, stageId, reason);
      
      // Update the quote locally
      setQuotes(quotes.map(q => 
        q.id === selectedQuoteForStage.id 
          ? { 
              ...q, 
              workflow_stage_id: stageId,
              workflow_stage_name: workflowStages.find(s => s.id === stageId)?.name,
              workflow_stage_color: workflowStages.find(s => s.id === stageId)?.color
            } 
          : q
      ));
      
      setShowStageModal(false);
      setSelectedQuoteForStage(null);
    } catch (error) {
      console.error('Failed to move quote stage:', error);
      alert('Failed to move quote to new stage. Please try again.');
    }
  };

  const handleConvertToItinerary = async () => {
    if (!selectedQuoteForConversion) return;

    try {
      setConvertingQuoteId(selectedQuoteForConversion.id);
      await quotesService.convertToItinerary(selectedQuoteForConversion.id);
      
      // Update the quote status locally to show it's been converted
      setQuotes(quotes.map(q => 
        q.id === selectedQuoteForConversion.id ? { ...q, status: 'converted' } : q
      ));
      
      // Close dialog
      setShowConvertDialog(false);
      setSelectedQuoteForConversion(null);
      
      // Navigate to itineraries
      navigate('/agent/itineraries');
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.error || 'Failed to convert quote to itinerary');
    } finally {
      setConvertingQuoteId(null);
    }
  };

  const openConvertDialog = (quote) => {
    setSelectedQuoteForConversion(quote);
    setShowConvertDialog(true);
  };

  const closeConvertDialog = () => {
    setShowConvertDialog(false);
    setSelectedQuoteForConversion(null);
  };

  const openPaymentDetailsModal = (quote) => {
    setSelectedQuoteForPaymentDetails(quote);
    setShowPaymentDetailsModal(true);
  };

  const closePaymentDetailsModal = () => {
    setShowPaymentDetailsModal(false);
    setSelectedQuoteForPaymentDetails(null);
  };

  // Check if quote can be converted
  const canConvertToItinerary = (quote) => {
    return quote.status && 
           quote.status !== 'draft' && 
           quote.status !== 'converted' &&
           quote.status !== 'expired';
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStage = stageFilter === 'all' || 
      (stageFilter === 'none' && !quote.workflow_stage_id) ||
      (quote.workflow_stage_id && quote.workflow_stage_id.toString() === stageFilter);
    const matchesSource = sourceFilter === 'all' || quote.source === sourceFilter;
    
    return matchesSearch && matchesStage && matchesSource;
  });

  // Use TravelieTheme status badge styling
  const getStatusBadgeStyle = (status) => {
    return {
      ...travelieTheme.getStatusBadgeStyle(status),
      display: 'inline-block',
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      borderRadius: travelieTheme.components.badge.borderRadius,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      textTransform: 'capitalize'
    };
  };

  const getStatusText = (status) => {
    const statusMap = {
      draft: 'Draft',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      deposit_paid: 'Deposit Paid',
      paid: 'Paid'
    };
    return statusMap[status] || status;
  };

  const getSourceBadge = (source) => {
    if (source === 'website_contact') {
      return (
        <span 
          style={{
            display: 'inline-block',
            padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
            background: '#DBEAFE',
            color: '#1E40AF',
            borderRadius: travelieTheme.components.badge.borderRadius,
            fontSize: travelieTheme.typography.fontSize.xs,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            marginLeft: travelieTheme.spacing.xs
          }}
          title="Quote created from website contact form"
        >
          🌐 Website
        </span>
      );
    }
    return null;
  };

  const getSourceText = (source) => {
    const sourceMap = {
      manual: 'Manual',
      website_contact: 'Website Contact',
      api: 'API',
      import: 'Import'
    };
    return sourceMap[source] || source;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        fontSize: travelieTheme.typography.fontSize.lg,
        color: travelieTheme.colors.text.secondary
      }}>
        Loading quotes...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: travelieTheme.spacing['2xl'],
        background: '#FEE2E2',
        color: '#991B1B',
        borderRadius: travelieTheme.components.card.borderRadius,
        margin: travelieTheme.spacing['2xl']
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? travelieTheme.spacing.lg : travelieTheme.spacing['3xl']
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: travelieTheme.spacing['2xl'],
        flexWrap: 'wrap',
        gap: travelieTheme.spacing.md
      }}>
        <h1 style={{
          fontSize: travelieTheme.typography.fontSize['2xl'],
          fontWeight: travelieTheme.typography.fontWeight.bold,
          color: travelieTheme.colors.text.primary,
          margin: 0
        }}>Travel Quotes</h1>
        <button 
          style={getButtonStyle('primary')}
          onClick={() => navigate('/agent/quotes/new')}
        >
          Create New Quote
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: travelieTheme.spacing.md,
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <input
          type="text"
          placeholder="Search quotes..."
          style={inputStyle}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          style={inputStyle}
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
        >
          <option value="all">All Stages</option>
          <option value="none">No Stage Assigned</option>
          {workflowStages.map((stage) => (
            <option key={stage.id} value={stage.id.toString()}>
              {stage.name}
            </option>
          ))}
        </select>
        <select
          style={inputStyle}
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          <option value="all">All Sources</option>
          <option value="manual">Manual</option>
          <option value="website_contact">Website Contact</option>
          <option value="api">API</option>
          <option value="import">Import</option>
        </select>
      </div>

      {filteredQuotes.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: travelieTheme.spacing['4xl'],
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.shadow
        }}>
          <p style={{
            fontSize: travelieTheme.typography.fontSize.lg,
            color: travelieTheme.colors.text.secondary,
            marginBottom: travelieTheme.spacing.lg
          }}>No quotes found.</p>
          {quotes.length === 0 && (
            <button 
              style={getButtonStyle('primary')}
              onClick={() => navigate('/agent/quotes/new')}
            >
              Create Your First Quote
            </button>
          )}
        </div>
      ) : (
        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.table.borderRadius,
          boxShadow: travelieTheme.components.card.shadow,
          overflow: isMobile ? 'auto' : 'hidden'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: travelieTheme.components.table.headerBackground,
                borderBottom: travelieTheme.components.table.border
              }}>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Quote #</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Title</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Client</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Travel Dates</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Total</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Stage</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Created</th>
                <th style={{
                  padding: travelieTheme.components.table.headerPadding,
                  textAlign: 'left',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map(quote => (
                <tr 
                  key={quote.id} 
                  data-source={quote.source}
                  style={{
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
                    transition: `background-color ${travelieTheme.transitions.fast}`,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = travelieTheme.components.table.rowHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary
                  }}>{quote.quote_number}</td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm
                  }}>
                    <strong style={{ color: travelieTheme.colors.text.primary }}>{quote.title}</strong>
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm
                  }}>
                    <div style={{ color: travelieTheme.colors.text.primary }}>{quote.client_name}</div>
                    <small style={{ color: travelieTheme.colors.text.secondary }}>{quote.client_email}</small>
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary
                  }}>
                    {quote.travel_start_date && quote.travel_end_date ? (
                      <span>
                        {formatDateRange(quote.travel_start_date, quote.travel_end_date)}
                      </span>
                    ) : (
                      <span style={{ color: travelieTheme.colors.text.muted }}>Not set</span>
                    )}
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.text.primary
                  }}>
                    ${(parseFloat(quote.total_amount) || 0).toFixed(2)}
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm
                  }}>
                    {quote.workflow_stage_name && (
                      <span 
                        style={{ 
                          display: 'inline-block',
                          padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                          backgroundColor: `${quote.workflow_stage_color}20`,
                          color: quote.workflow_stage_color,
                          borderColor: quote.workflow_stage_color,
                          border: '1px solid',
                          borderRadius: travelieTheme.components.badge.borderRadius,
                          fontSize: travelieTheme.typography.fontSize.xs,
                          fontWeight: travelieTheme.typography.fontWeight.medium
                        }}
                      >
                        {quote.workflow_stage_name}
                      </span>
                    )}
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.secondary
                  }}>
                    {new Date(quote.created_at).toLocaleDateString()}
                  </td>
                  <td style={{
                    padding: travelieTheme.components.table.cellPadding
                  }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: travelieTheme.spacing.xs
                    }}>
                      <button
                        style={getButtonStyle('info', 'small')}
                        onClick={() => navigate(`/agent/quotes/${quote.id}/preview`)}
                        title="View quote preview with client selections"
                      >
                        👁️ View Quote
                      </button>
                      <button
                        style={getButtonStyle('info', 'small')}
                        onClick={() => handleViewMessages(quote)}
                        title="View and reply to client messages"
                      >
                        💬 Messages
                      </button>
                      <button
                        style={getButtonStyle('purple', 'small')}
                        onClick={() => handleMoveStage(quote)}
                        title="Move to different workflow stage"
                      >
                        Move Stage
                      </button>
                      <button
                        style={getButtonStyle('secondary', 'small')}
                        onClick={() => navigate(`/agent/quotes/${quote.id}`)}
                      >
                        Edit
                      </button>
                      <button
                        style={getButtonStyle('secondary', 'small', duplicatingQuoteId === quote.id)}
                        onClick={() => handleDuplicate(quote.id)}
                        disabled={duplicatingQuoteId === quote.id}
                        title="Create a draft copy of this quote"
                      >
                        {duplicatingQuoteId === quote.id ? 'Duplicating...' : 'Duplicate'}
                      </button>
                      {quote.status === 'draft' && (
                        <>
                          <button
                            style={getButtonStyle('primary', 'small')}
                            onClick={() => handleSendQuote(quote.id)}
                          >
                            Send
                          </button>
                          <button
                            style={getButtonStyle('secondary', 'small')}
                            onClick={() => handleMarkAsSent(quote.id)}
                            title="Mark as sent without sending email"
                          >
                            Mark as Sent
                          </button>
                        </>
                      )}
                      {canConvertToItinerary(quote) && (
                        <button
                          style={getButtonStyle('success', 'small', convertingQuoteId === quote.id)}
                          onClick={() => openConvertDialog(quote)}
                          disabled={convertingQuoteId === quote.id}
                        >
                          {convertingQuoteId === quote.id ? 'Converting...' : 'Convert to Itinerary'}
                        </button>
                      )}
                      {quote.status === 'converted' && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                          background: travelieTheme.colors.status.successLight,
                          color: travelieTheme.colors.status.success,
                          borderRadius: travelieTheme.components.badge.borderRadius,
                          fontSize: travelieTheme.typography.fontSize.xs,
                          fontWeight: travelieTheme.typography.fontWeight.medium
                        }}>
                          ✓ Converted
                        </span>
                      )}
                      {quote.hasPayments && (
                        <button
                          style={getButtonStyle('info', 'small')}
                          onClick={() => openPaymentDetailsModal(quote)}
                        >
                          View Payments ({quote.payment_count})
                        </button>
                      )}
                      <button
                        style={getButtonStyle('danger', 'small')}
                        onClick={() => handleDelete(quote.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Convert to Itinerary Confirmation Dialog */}
      {showConvertDialog && selectedQuoteForConversion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: travelieTheme.spacing.lg
        }} onClick={closeConvertDialog}>
          <div style={{
            background: travelieTheme.colors.neutral.card,
            borderRadius: travelieTheme.components.card.borderRadius,
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: travelieTheme.spacing['2xl'],
              borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: travelieTheme.typography.fontSize.xl,
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.text.primary,
                margin: 0
              }}>Convert Quote to Itinerary</h2>
              <button style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: travelieTheme.colors.text.secondary,
                padding: 0,
                width: '32px',
                height: '32px'
              }} onClick={closeConvertDialog}>&times;</button>
            </div>
            <div style={{ padding: travelieTheme.spacing['2xl'] }}>
              <div style={{
                background: '#FEF3C7',
                border: `1px solid ${travelieTheme.colors.status.warning}`,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg,
                marginBottom: travelieTheme.spacing.lg
              }}>
                <p style={{
                  fontWeight: travelieTheme.typography.fontWeight.bold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.sm
                }}>Important:</p>
                <p style={{ color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.sm }}>This will convert your Travel Quote to an Itinerary. We recommend doing this only after you've received payment from the Client.</p>
                <p style={{ color: travelieTheme.colors.text.primary, margin: 0 }}>If you don't need to receive payment for this Travel Quote or already have, please proceed.</p>
              </div>
              <div style={{
                background: travelieTheme.colors.neutral.background,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.lg,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.md
                }}>Quote Details:</h3>
                <p style={{ color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.xs }}><strong>Title:</strong> {selectedQuoteForConversion.title}</p>
                <p style={{ color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.xs }}><strong>Client:</strong> {selectedQuoteForConversion.client_name}</p>
                <p style={{ color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.xs }}><strong>Total Amount:</strong> ${(parseFloat(selectedQuoteForConversion.total_amount) || 0).toFixed(2)}</p>
                <p style={{ color: travelieTheme.colors.text.primary, margin: 0 }}><strong>Status:</strong> {getStatusText(selectedQuoteForConversion.status)}</p>
              </div>
            </div>
            <div style={{
              padding: travelieTheme.spacing['2xl'],
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: travelieTheme.spacing.md
            }}>
              <button 
                style={getButtonStyle('secondary')}
                onClick={closeConvertDialog}
              >
                Cancel
              </button>
              <button 
                style={getButtonStyle('primary', 'medium', convertingQuoteId === selectedQuoteForConversion.id)}
                onClick={handleConvertToItinerary}
                disabled={convertingQuoteId === selectedQuoteForConversion.id}
              >
                {convertingQuoteId === selectedQuoteForConversion.id ? 'Converting...' : 'Confirm Conversion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Move Modal */}
      {showStageModal && selectedQuoteForStage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: travelieTheme.spacing.lg
        }} onClick={() => setShowStageModal(false)}>
          <div style={{
            background: travelieTheme.colors.neutral.card,
            borderRadius: travelieTheme.components.card.borderRadius,
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: travelieTheme.spacing['2xl'],
              borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: travelieTheme.typography.fontSize.xl,
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.text.primary,
                margin: 0
              }}>Move Quote to Different Stage</h2>
              <button style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: travelieTheme.colors.text.secondary,
                padding: 0,
                width: '32px',
                height: '32px'
              }} onClick={() => setShowStageModal(false)}>&times;</button>
            </div>
            <div style={{ padding: travelieTheme.spacing['2xl'] }}>
              <div style={{
                background: travelieTheme.colors.neutral.background,
                borderRadius: travelieTheme.components.card.borderRadius,
                padding: travelieTheme.spacing.lg,
                marginBottom: travelieTheme.spacing.lg
              }}>
                <h3 style={{
                  fontSize: travelieTheme.typography.fontSize.lg,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.sm
                }}>Quote: {selectedQuoteForStage.title}</h3>
                <p style={{ color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.xs }}><strong>Client:</strong> {selectedQuoteForStage.client_name}</p>
                <p style={{ color: travelieTheme.colors.text.primary, margin: 0 }}><strong>Current Stage:</strong> {selectedQuoteForStage.workflow_stage_name || 'None'}</p>
              </div>
              <div>
                <h4 style={{
                  fontSize: travelieTheme.typography.fontSize.base,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  marginBottom: travelieTheme.spacing.md
                }}>Select New Stage:</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: travelieTheme.spacing.md
                }}>
                  {workflowStages.map((stage) => (
                    <button
                      key={stage.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.sm,
                        padding: travelieTheme.spacing.md,
                        border: `2px solid ${stage.color}`,
                        borderRadius: travelieTheme.components.card.borderRadius,
                        background: selectedQuoteForStage.workflow_stage_id === stage.id ? `${stage.color}20` : 'transparent',
                        cursor: selectedQuoteForStage.workflow_stage_id === stage.id ? 'not-allowed' : 'pointer',
                        opacity: selectedQuoteForStage.workflow_stage_id === stage.id ? 0.7 : 1,
                        transition: `all ${travelieTheme.transitions.fast}`,
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium
                      }}
                      onClick={() => handleMoveQuoteStage(stage.id)}
                      disabled={selectedQuoteForStage.workflow_stage_id === stage.id}
                      onMouseEnter={(e) => {
                        if (selectedQuoteForStage.workflow_stage_id !== stage.id) {
                          e.currentTarget.style.background = `${stage.color}10`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedQuoteForStage.workflow_stage_id !== stage.id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        background: stage.color,
                        flexShrink: 0
                      }}></div>
                      <span style={{ flex: 1, textAlign: 'left' }}>{stage.name}</span>
                      {selectedQuoteForStage.workflow_stage_id === stage.id && (
                        <span style={{
                          padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                          background: stage.color,
                          color: '#FFFFFF',
                          borderRadius: travelieTheme.components.badge.borderRadius,
                          fontSize: travelieTheme.typography.fontSize.xs,
                          fontWeight: travelieTheme.typography.fontWeight.medium
                        }}>Current</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{
              padding: travelieTheme.spacing['2xl'],
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                style={getButtonStyle('secondary')}
                onClick={() => setShowStageModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secure Payment Details Modal */}
      {showPaymentDetailsModal && selectedQuoteForPaymentDetails && (
        <SecurePaymentDetailsModal 
          isOpen={showPaymentDetailsModal}
          quote={selectedQuoteForPaymentDetails} 
          onClose={closePaymentDetailsModal} 
        />
      )}

      {/* Quote Messages Modal */}
      {showMessagesModal && selectedQuoteForMessages && (
        <QuoteMessagesModal 
          isOpen={showMessagesModal}
          quote={selectedQuoteForMessages} 
          onClose={closeMessagesModal} 
        />
      )}
    </div>
  );
};

export default QuotesList;
